import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';

const UnsharpMaskShader = {
  uniforms: {
    tDiffuse: { value: null },
    radius: { value: 0.5 },
    amount: { value: 0.5 },
    threshold: { value: 0.1 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float radius;
    uniform float amount;
    uniform float threshold;
    varying vec2 vUv;

    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      vec4 blur = vec4(0.0);
      float total = 0.0;

      for(float x = -radius; x <= radius; x += 1.0) {
        for(float y = -radius; y <= radius; y += 1.0) {
          vec2 offset = vec2(x, y) / 512.0;
          blur += texture2D(tDiffuse, vUv + offset);
          total += 1.0;
        }
      }

      blur /= total;
      vec4 diff = color - blur;
      float diffMagnitude = length(diff.rgb);

      if(diffMagnitude > threshold) {
        color += diff * amount;
      }

      gl_FragColor = color;
    }
  `,
};

export class UnsharpMaskPass extends ShaderPass {
  public enabled: boolean = true;
  public uniforms: {
    tDiffuse: { value: THREE.Texture | null };
    radius: { value: number };
    amount: { value: number };
    threshold: { value: number };
  };

  constructor() {
    const shader = {
      uniforms: {
        tDiffuse: { value: null },
        radius: { value: 0.5 },
        amount: { value: 1.0 },
        threshold: { value: 0.1 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float radius;
        uniform float amount;
        uniform float threshold;
        varying vec2 vUv;

        void main() {
          vec4 color = texture2D(tDiffuse, vUv);
          vec2 texelSize = 1.0 / vec2(textureSize(tDiffuse, 0));
          vec4 blur = vec4(0.0);
          float total = 0.0;

          for (float x = -radius; x <= radius; x += 1.0) {
            for (float y = -radius; y <= radius; y += 1.0) {
              vec2 offset = vec2(x, y) * texelSize;
              blur += texture2D(tDiffuse, vUv + offset);
              total += 1.0;
            }
          }

          blur /= total;
          float diff = length(color.rgb - blur.rgb);
          float factor = step(threshold, diff);
          vec3 sharpened = mix(color.rgb, color.rgb + (color.rgb - blur.rgb) * amount, factor);
          gl_FragColor = vec4(sharpened, color.a);
        }
      `,
    };

    super(shader);
    this.uniforms = (this as any).material.uniforms;
  }
}
